<vs-card *ngIf="claim && (isOpen || history.length)">
  <vs-card-header
    [config]="{ title: 'Claim Authorization', subtitle: 'The financial decision of record for this claim.' }"
  >
    <vs-badge slot="after-title" [theme]="active ? 'success' : 'default'">
      {{ !active ? 'Not authorized' : active.netPayable === 0 ? 'Authorized · $0' : 'Authorized' }}
    </vs-badge>

    <button
      *ngIf="canStart"
      vs-button
      slot="end"
      theme="primary"
      [disabled]="!isReady"
      (click)="dialog.open('authorize')"
    >
      <vs-icon name="check-verified-02"></vs-icon> Authorize
    </button>

    <button
      *ngIf="canReverse && isOpen && active && !claim.hasSentPayments"
      vs-button
      slot="end"
      appearance="outline"
      (click)="dialog.open('reverse', active)"
    >
      <vs-icon name="reverse-left"></vs-icon> Reverse
    </button>
  </vs-card-header>

  <!-- Not authorized, or new jobs waiting for a supplemental: checklist and amount -->
  <div *ngIf="(!claim.frozen || hasNewJob) && isOpen" class="grid grid-cols-1 md:grid-cols-2 gap-6 mx-6 my-4">
    <div class="flex flex-col gap-3">
      <p class="text-sm font-medium text-gray-700">Pre-authorization checklist</p>

      <div *ngFor="let row of checklist" class="flex items-start gap-2">
        <vs-icon
          size="18"
          class="shrink-0 mt-0.5"
          [name]="row.isDone ? 'check-circle' : 'alert-circle'"
          [class.text-success-500]="row.isDone"
          [class.text-warn-500]="!row.isDone"
        ></vs-icon>
        <div class="flex flex-col">
          <p class="text-sm" [class.text-gray-900]="row.isDone" [class.text-gray-700]="!row.isDone">{{ row.label }}</p>
          <p *ngIf="row.hint" class="text-xs text-gray-500">{{ row.hint }}</p>
        </div>
      </div>
    </div>

    <div *ngIf="totals" class="flex flex-col gap-1 self-start w-full border rounded-xl px-4 py-3">
      <p class="text-sm text-gray-500">
        {{ hasNewJob ? 'Additional amount for the new jobs' : 'Final authorized amount' }}
      </p>
      <p class="text-2xl font-semibold text-gray-900">{{ formatAmount(totals.netPayable, totals.currency) }}</p>
      <p class="text-xs text-gray-500">
        {{ formatAmount(totals.authorizedGross, totals.currency) }} authorized gross<ng-container *ngIf="totals.tax">
          (incl. {{ formatAmount(totals.tax, totals.currency) }} tax)</ng-container
        >
        − {{ formatAmount(totals.deductible, totals.currency) }} deductible
      </p>
    </div>
  </div>

  <!-- Authorized: the first authorization and its supplementals, oldest first -->
  <ng-container *ngFor="let authorization of authorizations; let index = index; let first = first; let last = last">
    <vs-divider *ngIf="hasNewJob || !first"></vs-divider>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mx-6 my-4">
      <div
        *ngIf="authorizations.length > 1"
        class="col-span-2 md:col-span-4 flex flex-wrap items-center justify-between gap-2"
      >
        <div class="flex items-center gap-2">
          <vs-icon
            size="18"
            class="shrink-0 text-primary-500"
            [name]="first ? 'check-verified-02' : 'file-plus-03'"
          ></vs-icon>
          <p class="text-sm font-semibold text-gray-900">
            {{ first ? 'Original authorization' : 'Supplemental ' + index }}
          </p>
        </div>
        <vs-badge *ngIf="statusOf(authorization) as status" [theme]="status.theme">{{ status.text }}</vs-badge>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Reference</p>
        <p class="text-sm text-gray-900">{{ authorization.reference }}</p>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Net payable</p>
        <p class="text-sm font-semibold text-gray-900">
          {{ formatAmount(authorization.netPayable, authorization.currency) }}
        </p>
        <p class="text-xs text-gray-500">
          {{ formatAmount(authorization.authorizedGross, authorization.currency) }} gross<ng-container
            *ngIf="authorization.tax"
          >
            (incl. {{ formatAmount(authorization.tax, authorization.currency) }} tax)</ng-container
          >
          − {{ formatAmount(authorization.deductible, authorization.currency) }} deductible
        </p>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Authorized by</p>
        <p class="text-sm text-gray-900">
          {{ authorization.createdBy?.displayName || authorization.createdBy?.email || '–' }}
        </p>
      </div>
      <div class="flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Authorized on</p>
        <p class="text-sm text-gray-900">{{ authorization.created | date: 'medium' }}</p>
      </div>
      <div *ngIf="payeeLabel(authorization) as payee" class="flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Payee</p>
        <p class="text-sm text-gray-900">{{ payee }}</p>
      </div>
      <ng-container *ngIf="balanceOf(authorization) as balance">
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-gray-700">Paid</p>
          <p class="text-sm text-gray-900">{{ formatAmount(balance.paid, authorization.currency) }}</p>
        </div>
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-gray-700">Remaining</p>
          <p class="text-sm text-gray-900">{{ formatAmount(balance.remaining, authorization.currency) }}</p>
        </div>
      </ng-container>
      <div *ngIf="authorization.reason" class="col-span-2 md:col-span-4 flex flex-col gap-1">
        <p class="text-sm font-medium text-gray-700">Reason</p>
        <p class="text-sm text-gray-900 whitespace-pre-line">{{ authorization.reason }}</p>
      </div>
      <p
        *ngIf="last && canReverse && isOpen && claim.hasSentPayments"
        class="col-span-2 md:col-span-4 text-xs text-gray-500"
      >
        This authorization can't be reversed because a payment was issued. New jobs can be added and authorized on their
        own.
      </p>
    </div>
  </ng-container>

  <!-- History, oldest first -->
  <ng-container *ngIf="history.length">
    <vs-divider></vs-divider>
    <div class="flex flex-col gap-2 mx-6 my-4">
      <p class="text-sm font-medium text-gray-700">History</p>

      <div *ngFor="let record of history" class="flex items-start justify-between gap-4 text-sm">
        <div class="flex items-start gap-2">
          <vs-icon
            size="16"
            class="shrink-0 mt-0.5"
            [name]="record.action === actions.AUTHORIZED ? 'check-verified-02' : 'reverse-left'"
            [class.text-success-500]="record.action === actions.AUTHORIZED"
            [class.text-warn-500]="record.action === actions.REVERSED"
          ></vs-icon>
          <div class="flex flex-col">
            <p class="text-gray-900">
              {{ record.action === actions.AUTHORIZED ? 'Authorized' : 'Reversed' }}
              <span class="text-gray-500">
                · {{ record.reference
                }}<ng-container *ngIf="payeeLabel(record) as payee"> · to {{ payee }}</ng-container> · by
                {{ record.createdBy?.displayName || record.createdBy?.email || '–' }}
              </span>
            </p>
            <p *ngIf="record.reason" class="text-xs text-gray-500">{{ record.reason }}</p>
          </div>
        </div>

        <div class="flex flex-col items-end shrink-0">
          <p class="text-gray-900">{{ formatAmount(record.netPayable, record.currency) }}</p>
          <p class="text-xs text-gray-500">{{ record.created | date: 'mediumDate' }}</p>
        </div>
      </div>
    </div>
  </ng-container>
</vs-card>

<ws-claim-authorization-dialog #dialog [claim]="claim" (saved)="changed.emit()"></ws-claim-authorization-dialog>
